import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useLocation } from 'react-router-dom';
import Notifications from './notifications';
import Request from './request';
import i18n from './i18n';
import Loader from './loader';
import LoggedUserContext from '../contextProviders/LoggedUserContext';

function useLoader(
  url,
  defaultError,
  initialData = null,
  dependencies = [url],
  containerId,
) {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(initialData);

  useEffect(() => {
    setIsLoading(true);
    new Request()
      .get(url, undefined, true)
      .done(result => {
        setData(result.data);
        setIsLoading(false);
      })
      .fail(err => {
        setIsLoading(false);
        Notifications.showErrorFromRequest(err, defaultError);
      });
  }, dependencies);

  const loader = useRef(containerId ? new Loader(`#${containerId}`) : null);
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  return { isLoading, data, setData };
}

function useCards(_csrf, containerId, propCards = [], onChange) {
  const { loggedUser, setLoggedUser } = useContext(LoggedUserContext);

  const reloadLoggedUser = () =>
    new Promise(resolve => {
      new Request()
        .get(`/user/${loggedUser._id}/json`)
        .done(({ data }) => {
          setLoggedUser(data);
          resolve();
        })
        .fail(err => {
          Notifications.showErrorFromRequest(err, 'error_fetchProfile');
          resolve();
        });
    });

  const loader = useRef(null);

  const performRequest = (method, path, params, errorKey, onSuccess) => {
    loader.current.show();
    const options = {
      method,
      url: path,
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'License',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    };
    if (method === 'get') {
      options.params = params;
    } else {
      options.data = params;
    }
    try {
      axios(options)
        .then(({ data }) => {
          const done = () => {
            loader.current.hide();
            onSuccess(data);
          };

          if (errorKey === 'error_cardAdd') {
            reloadLoggedUser().finally(done);
          } else {
            done();
          }
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err, errorKey);
          loader.current.hide();
        });
      // result
      //   .done(data => {
      //     const done = () => {
      //       loader.current.hide();
      //       onSuccess(data);
      //     };
      //
      //     if (errorKey === 'error_cardAdd') {
      //       reloadLoggedUser().finally(done);
      //     } else {
      //       done();
      //     }
      //   })
      //   .fail(err => {
      //     Notifications.showErrorFromRequest(err, errorKey);
      //     loader.current.hide();
      //   });
    } catch (err) {
      Notifications.showNotificationError(i18n.__('error'), err);
      loader.current.hide();
    }
  };

  const [cards, setCards] = useState(propCards.slice());
  useEffect(() => {
    loader.current = new Loader('#credit-card-container');
    performRequest(
      'get',
      '/api/credit-card/list?isContextAcademy=true',
      {},
      'error_cardSelect',
      ({ data }) => setCards(data),
    );
  }, []);

  useEffect(() => {
    if (onChange) {
      if (cards.length !== propCards.length) {
        onChange(cards.slice());
      } else {
        for (const [i, card] of cards.entries()) {
          if (card.isDefault && !propCards[i].isDefault) {
            onChange(cards.slice());
            break;
          }
        }
      }
    }
  }, [cards]);

  const onSelect = cardId => {
    const onSuccess = () => {
      setCards(prev =>
        prev.map(c =>
          c.id === cardId
            ? { ...c, isDefault: true }
            : { ...c, isDefault: false },
        ),
      );
    };

    performRequest(
      'post',
      '/api/credit-card/default?isContextAcademy=true',
      { cardId },
      'error_cardSelect',
      onSuccess,
    );
  };

  const onAdd = ({ token: cardToken }) => {
    const onSuccess = ({ data: card }) => {
      setCards(prev =>
        prev
          .concat(card)
          .map(c =>
            c.id === card.id
              ? { ...c, isDefault: true }
              : { ...c, isDefault: false },
          ),
      );
    };

    performRequest(
      'post',
      '/api/credit-card?isContextAcademy=true',
      { token: cardToken },
      'error_cardAdd',
      onSuccess,
    );
  };

  const onRemove = cardId => {
    const onSuccess = () => setCards(prev => prev.filter(c => c.id !== cardId));

    performRequest(
      'delete',
      '/api/credit-card',
      { cardId },
      'error_cardRemove',
      onSuccess,
    );
  };

  return {
    cards,
    handleAddCard: onAdd,
    handleRemoveCard: onRemove,
    handleSelectCard: onSelect,
  };
}

function useLikesAndComments(setData, _csrf) {
  const [commentsModal, setCommentsModal] = useState({ show: false, id: null });

  const hideCommentsModal = () => setCommentsModal({ show: false, id: null });

  const showCommentsModal = event =>
    setCommentsModal({ show: true, id: event._id });

  const addLiked = eventId => {
    setData(oldData =>
      oldData.map(e =>
        e._id !== eventId ? e : { ...e, isLiked: true, likes: e.likes + 1 },
      ),
    );
  };

  const revertLiked = eventId => {
    setData(oldData =>
      oldData.map(e =>
        e._id !== eventId ? e : { ...e, isLiked: false, likes: e.likes - 1 },
      ),
    );
  };

  const addComment = eventId => {
    setData(oldData =>
      oldData.map(e =>
        e._id !== eventId ? e : { ...e, comments: e.comments + 1 },
      ),
    );
  };

  const revertComment = eventId => {
    setData(oldData =>
      oldData.map(e =>
        e._id !== eventId ? e : { ...e, comments: e.comments - 1 },
      ),
    );
  };

  const performRequest = (url, params, defaultError) =>
    new Promise((resolve, reject) => {
      new Request(_csrf)
        .post(url, params)
        .done(resolve)
        .fail(err => {
          Notifications.showErrorFromRequest(err, defaultError);
          reject();
        });
    });

  const handleAddLike = event => {
    if (!event.isLiked) {
      addLiked(event._id);
      performRequest(
        `/challenges/event/${event._id}/like`,
        undefined,
        i18n.__('error_eventLike'),
      ).catch(() => revertLiked(event._id));
    }
  };

  const handleAddComment = (eventId, message) => {
    addComment(eventId);
    performRequest(
      `/challenges/event/${eventId}/comment`,
      { message },
      i18n.__('error_eventComment'),
    ).catch(() => revertComment(eventId));
  };

  return {
    commentsModal,
    showCommentsModal,
    hideCommentsModal,
    handleAddLike,
    handleAddComment,
  };
}

function useScrollAnchor() {
  const contentElement = useRef(null);

  const scrollToContent = () => {
    if (contentElement.current) {
      const headerOffset = window.matchMedia('(max-width: 1024px)').matches
        ? 85
        : 120;
      const elementPosition =
        contentElement.current.getBoundingClientRect().top;
      window.scrollBy({
        top: elementPosition - headerOffset,
        behavior: 'smooth',
      });
    }
  };

  return { contentElement, scrollToContent };
}

function useSwipeable(receivedProps) {
  const defaultProps = {
    preventDefaultTouchmoveEvent: false,
    delta: 10,
    rotationAngle: 0,
  };

  const initialState = {
    xy: [0, 0],
    swiping: false,
    eventData: undefined,
    start: undefined,
  };

  const DIRECTION = {
    LEFT: 'Left',
    RIGHT: 'Right',
    UP: 'Up',
    DOWN: 'Down',
  };
  const EVENT = {
    TOUCH_START: 'touchstart',
    TOUCH_MOVE: 'touchmove',
    TOUCH_END: 'touchend',
  };

  function getDirection(absX, absY, deltaX, deltaY) {
    if (absX > absY) {
      if (deltaX > 0) {
        return DIRECTION.LEFT;
      }
      return DIRECTION.RIGHT;
    }
    if (deltaY > 0) {
      return DIRECTION.UP;
    }
    return DIRECTION.DOWN;
  }

  function rotateXYByAngle(pos, angle) {
    if (angle === 0) return pos;
    const angleInRadians = (Math.PI / 180) * angle;
    const x =
      pos[0] * Math.cos(angleInRadians) + pos[1] * Math.sin(angleInRadians);
    const y =
      pos[1] * Math.cos(angleInRadians) - pos[0] * Math.sin(angleInRadians);
    return [x, y];
  }

  function getHandlers(set) {
    const onStart = event => {
      // if more than a single touch don't track, for now...
      if (event.touches && event.touches.length > 1) return;

      set((state, props) => {
        const { clientX, clientY } = event.touches ? event.touches[0] : event;
        const xy = rotateXYByAngle([clientX, clientY], props.rotationAngle);
        return {
          ...state,
          ...initialState,
          eventData: { initial: [...xy], first: true },
          xy,
          start: event.timeStamp || 0,
        };
      });
    };

    const onMove = event => {
      set((state, props) => {
        if (
          !state.xy[0] ||
          !state.xy[1] ||
          (event.touches && event.touches.length > 1)
        ) {
          return state;
        }
        const { clientX, clientY } = event.touches ? event.touches[0] : event;
        const [x, y] = rotateXYByAngle([clientX, clientY], props.rotationAngle);
        const deltaX = state.xy[0] - x;
        const deltaY = state.xy[1] - y;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        const time = (event.timeStamp || 0) - state.start;
        const velocity = Math.sqrt(absX * absX + absY * absY) / (time || 1);

        // if swipe is under delta and we have not started to track a swipe: skip update
        if (absX < props.delta && absY < props.delta && !state.swiping) {
          return state;
        }

        const dir = getDirection(absX, absY, deltaX, deltaY);
        const eventData = {
          ...state.eventData,
          event,
          absX,
          absY,
          deltaX,
          deltaY,
          velocity,
          dir,
        };

        if (props.onSwiping) {
          props.onSwiping(eventData);
        }

        // track if a swipe is cancelable(handler for swiping or swiped(dir) exists)
        // so we can call preventDefault if needed
        let cancelablePageSwipe = false;
        if (props.onSwiping || props.onSwiped || props[`onSwiped${dir}`]) {
          cancelablePageSwipe = true;
        }

        if (
          cancelablePageSwipe &&
          props.preventDefaultTouchmoveEvent &&
          event.cancelable
        ) {
          event.preventDefault();
        }

        // first is now always false
        return {
          ...state,
          eventData: { ...eventData, first: false },
          swiping: true,
        };
      });
    };

    const onEnd = event => {
      set((state, props) => {
        let eventData;
        if (state.swiping) {
          eventData = { ...state.eventData, event };

          if (props.onSwiped) {
            props.onSwiped(eventData);
          }

          if (props[`onSwiped${eventData.dir}`]) {
            props[`onSwiped${eventData.dir}`](eventData);
          }
        }
        return { ...state, ...initialState, eventData };
      });
    };

    const attachTouch = el => {
      if (!el || !el.addEventListener) {
        return undefined;
      }
      // attach touch event listeners and handlers
      const tls = [
        [EVENT.TOUCH_START, onStart],
        [EVENT.TOUCH_MOVE, onMove],
        [EVENT.TOUCH_END, onEnd],
      ];
      tls.forEach(([e, h]) => el.addEventListener(e, h));
      // return properly scoped cleanup method for removing listeners
      return () => tls.forEach(([e, h]) => el.removeEventListener(e, h));
    };

    const onRef = el => {
      // "inline" ref functions are called twice on render, once with null then again with DOM element
      // ignore null here
      if (el === null) return;
      set(state => {
        // if the same DOM el as previous just return state
        if (state.el === el) return state;

        const addState = {};
        // if new DOM el clean up old DOM and reset cleanUpTouch
        if (state.el && state.el !== el && state.cleanUpTouch) {
          state.cleanUpTouch();
          addState.cleanUpTouch = null;
        }
        // only attach if we want to track touch
        if (el) {
          addState.cleanUpTouch = attachTouch(el);
        }

        // store event attached DOM el for comparison, clean up, and re-attachment
        return { ...state, el, ...addState };
      });
    };

    return [onRef, attachTouch];
  }

  function updateTransientState(state, props, attachTouch) {
    const addState = {};
    // clean up touch handlers if no longer tracking touches
    if (!state.cleanUpTouch) {
      // attach/re-attach touch handlers
      if (state.el) {
        addState.cleanUpTouch = attachTouch(state.el);
      }
    }
    return { ...state, ...addState };
  }

  const transientState = useRef({ ...initialState, type: 'hook' });
  const transientProps = useRef();
  transientProps.current = { ...defaultProps, ...receivedProps };

  const [onRef, attachTouch] = useMemo(
    () =>
      getHandlers(cb => {
        transientState.current = cb(
          transientState.current,
          transientProps.current,
        );
      }),
    [],
  );

  transientState.current = updateTransientState(
    transientState.current,
    transientProps.current,
    attachTouch,
  );

  return onRef;
}

function useModal({ show = false, data = {} }) {
  const [showModal, setRawShowModal] = useState({ show, data });

  const setShowModal = value => {
    if (!value.show) {
      document
        .querySelectorAll('.modal-backdrop.fade.show')
        .forEach(element => element.parentNode.removeChild(element));
    }
    setRawShowModal(value);
  };
  return [showModal, setShowModal];
}

const useFocus = () => {
  const htmlElRef = useRef(null);
  const setFocus = (isFocus = 'default', timeout = 1000) => {
    if (htmlElRef.current) {
      switch (isFocus) {
        case 'focus':
          htmlElRef.current.focus();
          htmlElRef.current.className = '';
          break;
        case 'blur':
          return htmlElRef.current.blur();
        case 'default':
        default:
          htmlElRef.current.focus();
          setTimeout(() => {
            htmlElRef.current.blur();
            htmlElRef.current.className = '';
          }, timeout);
          break;
      }
    }
    return null;
  };

  return [htmlElRef, setFocus];
};

const useHost = () => {
  const [host, setHost] = useState('');
  useEffect(() => {
    setHost(window.location.origin);
  }, []);
  return host;
};

const useDimensions = () => {
  const [width, setWidth] = useState(null);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }
  useEffect(() => {
    setWidth(window.innerWidth);

    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  return { width, isMobile: width ? width < 455 : false };
};

function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef(null);

  const debouncedFunction = (...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
      timeoutRef.current = null;
    }, delay);
  };

  return debouncedFunction;
}

/**
 * Used to request job, and update status of the same
 * @param statusEndpoint {string} - The endpoint for checking the job status
 * @param jobMethod
 * @param _csrf {string} - CSRF-Token
 * @param onSuccess {function} - Handles the success case
 * @param onFailure {function} - Handles the failure case
 * @param progressMessage {function} - Returns indicative message of progress to user
 * @param retryInterval {number} - Interval to check the job status to API
 * @param loader - Loader to show progress
 * @returns {{loading: boolean, initiateJob: function}}
 */
const useJobStatus = ({
  statusEndpoint,
  jobMethod = 'get',
  _csrf,
  onSuccess,
  onFailure,
  progressMessage,
  retryInterval = 1000,
  loader,
}) => {
  const [loading, setLoading] = useState(false);

  const checkJobStatus = jobId => {
    axios
      .get(statusEndpoint(jobId))
      .then(({ data: response }) => {
        const { progress, finishedOn, failedReason, returnvalue } =
          response.data;
        loader.current.showProgressLoader(progressMessage(progress.toFixed(0)));

        if (finishedOn) {
          loader.current.hide();
          setLoading(false);
          if (failedReason) {
            onFailure(failedReason);
          } else {
            onSuccess(returnvalue);
          }
        } else {
          // Continue polling until the job finishes
          setTimeout(() => checkJobStatus(jobId), retryInterval);
        }
      })
      .catch(err => {
        loader.current.hide();
        setLoading(false);
        Notifications.showErrorFromRequest(err, 'error.general');
      });
  };

  const initiateJob = generateEndpoint => {
    axios[jobMethod](generateEndpoint, { _csrf })
      .then(({ data: response }) => {
        setLoading(true);
        loader.current.show({ message: progressMessage(0) });

        // Start job status polling after initiation
        setTimeout(() => checkJobStatus(response.data.id), retryInterval);
      })
      .catch(err => {
        console.log(err);
      });
  };

  return { loading, initiateJob };
};

const useSanitize = content => {
  const [sanitizedContent, setSanitizedContent] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined' && content?.length > 0) {
      // Sanitize the description prop
      const sanitized = DOMPurify.sanitize(content);
      setSanitizedContent(sanitized); // Set the sanitized description
    }
  }, [content]);

  return sanitizedContent;
};

const useQuery = () => new URLSearchParams(useLocation().search);

export {
  useLoader,
  useCards,
  useLikesAndComments,
  useScrollAnchor,
  useSwipeable,
  useModal,
  useFocus,
  useHost,
  useDimensions,
  useDebouncedCallback,
  useJobStatus,
  useSanitize,
  useQuery,
};
